import { faker } from '@faker-js/faker'
import { http, HttpResponse } from 'msw'
import { PROJECT_FULL_PROBLEM_TYPE } from '../src/components/projects/project-capacity.ts'
import {
  AddProjectMembershipsRequestSchema,
  CreateProjectRequestSchema,
  type Project,
  type ProjectMembership,
  type ProjectMembershipPaginatedResponse,
  type ProjectPaginatedResponse,
  PutProjectMembershipRequestSchema,
  type Repository,
  UpdateProjectRequestSchema,
} from '../src/utils/api-client/api-schemas.ts'
import { withAuth } from './handlers.ts'

// A stateful in-memory model of the Projects API, shared by dev:mocks, Storybook, vitest and
// Playwright. Membership lives on the store rather than on the repository fixtures so that a move or
// a removal is visible on the repositories list as well as on the project. Membership is a first-class
// HAL resource: a repository points at `/repositories/{id}/project-membership`, and the project it
// belongs to is reached by embedding it.

type StoredProject = { id: string; name: string; description?: string; created_at: string; capacity: number }
type StoredMembership = { projectId: string; created_at: string }

export type ProjectSeed = {
  id: string
  name: string
  description?: string
  /** Positions in the seeded repository list that start out in this project. */
  repositoryIndexes?: number[]
  created_at?: string
  repository_capacity?: number
}

const projects = new Map<string, StoredProject>()
/** Keyed by repository id; a repository belongs to at most one project. */
const memberships = new Map<string, StoredMembership>()
let repositories: Repository[] = []
let canManage = true

const forbidden = () => HttpResponse.text('forbidden', { status: 403 })

const projectLinks = (id: string): Project['_links'] => ({
  self: { href: `/api/v1/projects/${id}` },
  memberships: { href: `/api/v1/projects/${id}/memberships` },
  repositories: { href: `/api/v1/repositories?project=${id}` },
})

const memberRepositories = (projectId: string): Repository[] =>
  repositories
    .filter(repository => memberships.get(repository.id)?.projectId === projectId)
    .sort((a, b) => a.full_name.localeCompare(b.full_name))

const memberCount = (projectId: string) =>
  [...memberships.values()].filter(membership => membership.projectId === projectId).length

const nowIso = () => new Date().toISOString()

const pageOf = <T>(items: T[]) => ({
  _links: { self: { href: '' }, first: { href: '' } },
  page: { number: 0, size: items.length },
  total: items.length,
  _embedded: { items },
})

const toResponse = (
  project: StoredProject,
  { embedRepositories = false }: { embedRepositories?: boolean } = {}
): Project => ({
  _links: projectLinks(project.id),
  _templates: canManage ? { replace: { method: 'PUT' }, delete: { method: 'DELETE' } } : {},
  created_at: project.created_at,
  description: project.description,
  id: project.id,
  name: project.name,
  repository_capacity: project.capacity,
  repository_count: memberCount(project.id),
  ...(embedRepositories
    ? {
        _embedded: {
          repositories: pageOf(memberRepositories(project.id).map(repository => stampMembership(repository))),
        },
      }
    : {}),
})

/** The membership resource for a repository, or `undefined` when it is ungrouped. */
const membershipFor = (repositoryId: string, { embedProject = false } = {}): ProjectMembership | undefined => {
  const membership = memberships.get(repositoryId)
  const project = membership ? projects.get(membership.projectId) : undefined
  if (!membership || !project) {
    return undefined
  }
  return {
    _links: {
      self: { href: `/api/v1/repositories/${repositoryId}/project-membership` },
      project: { href: `/api/v1/projects/${project.id}` },
      repository: { href: `/api/v1/repositories/${repositoryId}` },
    },
    _templates: canManage ? { replace: { method: 'PUT' }, delete: { method: 'DELETE' } } : {},
    created_at: membership.created_at,
    ...(embedProject ? { _embedded: { project: toResponse(project) } } : {}),
  }
}

/**
 * Stamps a repository with its membership link, and with the embedded membership + project only when
 * the request asked for it. Embedding unconditionally is what let a caller forget `embed` and still
 * pass here — the gap then shows up against the real API, where it is a blank field in production.
 */
const stampMembership = (repository: Repository, { embed = true }: { embed?: boolean } = {}): Repository => {
  const membership = embed ? membershipFor(repository.id, { embedProject: true }) : undefined
  return {
    ...repository,
    _links: {
      ...repository._links,
      'project-membership': repository._links['project-membership'] ?? {
        href: `/api/v1/repositories/${repository.id}/project-membership`,
      },
    },
    _embedded: membership ? { ...repository._embedded, 'project-membership': membership } : repository._embedded,
  }
}

/** Whether a request's `embed` values ask for a repository's project membership. */
export const wantsMembershipEmbed = (request: Request) =>
  new URL(request.url).searchParams
    .getAll('embed')
    .some(
      value =>
        value === 'project-membership' ||
        value.endsWith('.project-membership') ||
        value.includes('project-membership.project')
    )

const paginate = <T>(items: T[], request: Request) => {
  const url = new URL(request.url)
  const number = Number(url.searchParams.get('page-number')) || 0
  const size = Number(url.searchParams.get('page-size')) || 10
  return {
    _links: { self: { href: url.pathname }, first: { href: url.pathname } },
    page: { number, size },
    total: items.length,
    _embedded: { items: items.slice(number * size, (number + 1) * size) },
  }
}

const membershipCollectionTemplates = () => (canManage ? { create: { method: 'POST' } } : {})

const notFound = (what: string) => HttpResponse.text(`${what} not found`, { status: 404 })

/** The capacity a seeded or created project gets unless its seed names another. */
const PROJECT_CAPACITY = 100

const capacityOf = (projectId: string) => projects.get(projectId)?.capacity ?? PROJECT_CAPACITY

const projectFullProblem = (projectId: string) => {
  const capacity = capacityOf(projectId)
  const remaining = Math.max(0, capacity - memberCount(projectId))
  return HttpResponse.json(
    {
      type: PROJECT_FULL_PROBLEM_TYPE,
      title: 'Project is full',
      detail:
        remaining === 0
          ? `This project already holds ${capacity} repositories. No membership was changed.`
          : `This project has room for ${remaining} more. No membership was changed.`,
      status: 409,
      capacity,
      remaining,
    },
    { status: 409, headers: { 'Content-Type': 'application/problem+json' } }
  )
}

/** The store's view of the repositories the other mocks serve, with membership stamped on. */
export const projectStore = {
  /** Every repository the store knows about, each carrying its membership link; embeds only when asked. */
  repositories: ({ embed = true }: { embed?: boolean } = {}): Repository[] =>
    repositories.map(repository => stampMembership(repository, { embed })),
  membershipFor,
  stampMembership,
  /** Narrows repositories to the `project` query values the list endpoint accepts: ids, or `ungrouped`. */
  filterByProject: <T extends Pick<Repository, 'id'>>(items: T[], projectParams: string[]): T[] => {
    if (projectParams.length === 0) {
      return items
    }
    return items.filter(item => {
      const membership = memberships.get(item.id)
      return membership
        ? projectParams.includes(membership.projectId)
        : projectParams.some(value => value.toLowerCase() === 'ungrouped')
    })
  },
  projects: (embedRepositories = false): Project[] =>
    [...projects.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(project => toResponse(project, { embedRepositories })),
  setCanManage: (value: boolean) => {
    canManage = value
  },
  reset: () => {
    projects.clear()
    memberships.clear()
    repositories = []
    canManage = true
  },
}

/**
 * Loads the store. Called once by the default handlers with the same repositories the repositories
 * mock serves; tests call it again with their own fixtures.
 */
export const seedProjectStore = ({
  repositories: seededRepositories,
  projects: seeds,
}: {
  repositories: Repository[]
  projects: ProjectSeed[]
}) => {
  projectStore.reset()
  repositories = seededRepositories
  for (const seed of seeds) {
    projects.set(seed.id, {
      id: seed.id,
      name: seed.name,
      description: seed.description,
      created_at: seed.created_at ?? '2026-09-12T10:22:00Z',
      capacity: seed.repository_capacity ?? PROJECT_CAPACITY,
    })
    for (const index of seed.repositoryIndexes ?? []) {
      const repository = seededRepositories[index]
      if (repository) {
        memberships.set(repository.id, { projectId: seed.id, created_at: seed.created_at ?? '2026-09-12T10:22:00Z' })
      }
    }
  }
}

/** The projects of the design prototype, spread over the first repositories of the seeded list. */
/** `count` consecutive positions in the seeded repository list, starting at `start`. */
const range = (start: number, count: number) => Array.from({ length: count }, (_, offset) => start + offset)

/**
 * Where the two cap-state projects take their members from. They are consecutive blocks, so the
 * estate `handlers.ts` seeds has to be at least this long — `seedProjectStore` silently skips an
 * index it has no repository for, which would leave a "full" project that is not actually full.
 */
const FULL_PROJECT_FIRST_INDEX = 23

const NEARLY_FULL_PROJECT_FIRST_INDEX = FULL_PROJECT_FIRST_INDEX + PROJECT_CAPACITY

export const PROTOTYPE_PROJECT_SEEDS: ProjectSeed[] = [
  {
    id: 'project-fmqpr',
    name: 'FMQPR Payments',
    description: 'Payments platform, ring-fenced',
    repositoryIndexes: [0, 1, 2, 3],
    created_at: '2026-09-12T10:22:00Z',
  },
  {
    id: 'project-retail',
    name: 'Retail Channels',
    description: 'Mobile and web front ends',
    repositoryIndexes: [4, 5, 6],
    created_at: '2026-08-28T16:15:00Z',
  },
  {
    id: 'project-treasury',
    name: 'Treasury Core',
    description: 'Ledger and settlement',
    repositoryIndexes: [7, 8],
  },
  {
    id: 'project-reporting',
    name: 'Regulatory Reporting',
    description: 'Stood up, not yet populated',
  },
  {
    id: 'project-shared',
    name: 'Shared Services',
    description: 'Platform libraries and internal tooling',
    repositoryIndexes: [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22],
    created_at: '2026-09-02T09:40:00Z',
  },
  // The two cap states, so both are reachable by clicking in `dev:mocks`: one project that is full, and
  // one with room for two, which refuses a selection of three with "has room for 2 more".
  {
    id: 'project-full',
    name: 'Full Project',
    description: 'At the cap — nothing more can be added',
    repositoryIndexes: range(FULL_PROJECT_FIRST_INDEX, PROJECT_CAPACITY),
    created_at: '2026-07-14T11:05:00Z',
  },
  {
    id: 'project-nearly-full',
    name: 'Nearly Full Project',
    description: 'Two places left',
    repositoryIndexes: range(NEARLY_FULL_PROJECT_FIRST_INDEX, PROJECT_CAPACITY - 2),
    created_at: '2026-07-21T08:30:00Z',
  },
]

const embedsRepositories = (request: Request) =>
  new URL(request.url).searchParams.getAll('embed').includes('items.repositories')

/** The `name` filter: a case-insensitive substring over project names, as the API matches it. */
const nameFilter = (request: Request) => (new URL(request.url).searchParams.get('name') ?? '').trim().toLowerCase()

export const getProjects = () =>
  http.get(
    '/api/v1/projects',
    withAuth(({ request }) => {
      const query = nameFilter(request)
      const matches = projectStore
        .projects(embedsRepositories(request))
        .filter(project => query === '' || project.name.toLowerCase().includes(query))
      return HttpResponse.json({
        ...paginate(matches, request),
        _templates: canManage ? { create: { method: 'POST', target: '/api/v1/projects' } } : {},
      } satisfies ProjectPaginatedResponse)
    })
  )

export const getProject = () =>
  http.get(
    '/api/v1/projects/:projectId',
    withAuth(({ params, request }) => {
      const project = projects.get(String(params.projectId))
      return project
        ? HttpResponse.json(toResponse(project, { embedRepositories: embedsRepositories(request) }))
        : notFound('Project')
    })
  )

const duplicateProblem = (requestedName: string) =>
  HttpResponse.json(
    {
      type: 'pixee:problems/api/projects/duplicate-name',
      title: 'Duplicate project name',
      detail: `A project named ${requestedName} already exists.`,
      status: 409,
    },
    { status: 409, headers: { 'Content-Type': 'application/problem+json' } }
  )

export const postProject = () =>
  http.post(
    '/api/v1/projects',
    withAuth(async ({ request }) => {
      if (!canManage) {
        return forbidden()
      }
      const body = CreateProjectRequestSchema.parse(await request.json())
      const name = body.name.trim()
      const duplicate = [...projects.values()].find(project => project.name.toLowerCase() === name.toLowerCase())
      if (duplicate) {
        return duplicateProblem(name)
      }
      const project: StoredProject = {
        id: faker.string.uuid(),
        name,
        description: body.description,
        created_at: nowIso(),
        capacity: PROJECT_CAPACITY,
      }
      projects.set(project.id, project)
      return HttpResponse.json(toResponse(project), {
        status: 201,
        headers: { Location: `/api/v1/projects/${project.id}` },
      })
    })
  )

export const putProject = () =>
  http.put(
    '/api/v1/projects/:projectId',
    withAuth(async ({ params, request }) => {
      if (!canManage) {
        return forbidden()
      }
      const project = projects.get(String(params.projectId))
      if (!project) {
        return notFound('Project')
      }
      const body = UpdateProjectRequestSchema.parse(await request.json())
      if (body.name !== undefined) {
        const name = body.name.trim()
        const duplicate = [...projects.values()].find(
          other => other.id !== project.id && other.name.toLowerCase() === name.toLowerCase()
        )
        if (duplicate) {
          return duplicateProblem(name)
        }
        project.name = name
      }
      if (body.description !== undefined) {
        project.description = body.description ?? undefined
      }
      return HttpResponse.json(toResponse(project))
    })
  )

export const deleteProject = () =>
  http.delete(
    '/api/v1/projects/:projectId',
    withAuth(({ params }) => {
      if (!canManage) {
        return forbidden()
      }
      const projectId = String(params.projectId)
      if (!projects.delete(projectId)) {
        return notFound('Project')
      }
      for (const [repositoryId, membership] of memberships) {
        if (membership.projectId === projectId) {
          memberships.delete(repositoryId)
        }
      }
      return new HttpResponse(null, { status: 204 })
    })
  )

export const getProjectMemberships = () =>
  http.get(
    '/api/v1/projects/:projectId/memberships',
    withAuth(({ params, request }) => {
      const projectId = String(params.projectId)
      if (!projects.has(projectId)) {
        return notFound('Project')
      }
      const items = memberRepositories(projectId)
        .map(repository => membershipFor(repository.id, { embedProject: true }))
        .filter((membership): membership is ProjectMembership => membership !== undefined)
      return HttpResponse.json({
        ...paginate(items, request),
        _templates: membershipCollectionTemplates(),
      } satisfies ProjectMembershipPaginatedResponse)
    })
  )

export const postProjectMemberships = () =>
  http.post(
    '/api/v1/projects/:projectId/memberships',
    withAuth(async ({ params, request }) => {
      if (!canManage) {
        return forbidden()
      }
      const projectId = String(params.projectId)
      if (!projects.has(projectId)) {
        return notFound('Project')
      }
      const body = AddProjectMembershipsRequestSchema.parse(await request.json())
      const requested = [...new Set(body.repository_ids)]
      // All-or-nothing, in the order `ProjectService.add` checks: every unknown id is collected and
      // refused before capacity is counted, and nothing is written unless both checks pass. A
      // repository already in this project costs it nothing.
      const unknown = requested.filter(id => !repositories.some(candidate => candidate.id === id))
      if (unknown.length > 0) {
        return HttpResponse.json(
          {
            type: 'pixee:problems/api/project-memberships/unknown-repositories',
            title: 'Unknown repositories',
            detail: `${unknown.length} of the requested repositories do not exist, so no membership was changed.`,
            status: 422,
            repository_ids: unknown,
          },
          { status: 422, headers: { 'Content-Type': 'application/problem+json' } }
        )
      }
      const newcomers = requested.filter(id => memberships.get(id)?.projectId !== projectId).length
      if (memberCount(projectId) + newcomers > capacityOf(projectId)) {
        return projectFullProblem(projectId)
      }
      for (const repositoryId of requested) {
        if (memberships.get(repositoryId)?.projectId !== projectId) {
          memberships.set(repositoryId, { projectId, created_at: nowIso() })
        }
      }
      const items = memberRepositories(projectId)
        .filter(repository => requested.includes(repository.id))
        .map(repository => membershipFor(repository.id, { embedProject: true }))
        .filter((membership): membership is ProjectMembership => membership !== undefined)
      return HttpResponse.json({
        _links: {
          self: { href: `/api/v1/projects/${projectId}/memberships` },
          first: { href: `/api/v1/projects/${projectId}/memberships` },
        },
        page: { number: 0, size: items.length },
        total: items.length,
        _embedded: { items },
        _templates: membershipCollectionTemplates(),
      } satisfies ProjectMembershipPaginatedResponse)
    })
  )

export const getRepositoryProject = () =>
  http.get(
    '/api/v1/repositories/:repositoryId/project-membership',
    withAuth(({ params }) => {
      const membership = membershipFor(String(params.repositoryId), { embedProject: true })
      return membership ? HttpResponse.json(membership) : notFound('Membership')
    })
  )

export const putRepositoryProjectMembership = () =>
  http.put(
    '/api/v1/repositories/:repositoryId/project-membership',
    withAuth(async ({ params, request }) => {
      if (!canManage) {
        return forbidden()
      }
      const repositoryId = String(params.repositoryId)
      const body = PutProjectMembershipRequestSchema.parse(await request.json())
      if (!projects.has(body.project_id)) {
        return HttpResponse.json(
          {
            type: 'pixee:problems/api/project-memberships/unknown-project',
            title: 'Unknown project',
            detail: `No project exists with the id ${body.project_id}.`,
            status: 422,
            project_id: body.project_id,
          },
          { status: 422, headers: { 'Content-Type': 'application/problem+json' } }
        )
      }
      const isAlreadyThere = memberships.get(repositoryId)?.projectId === body.project_id
      if (!isAlreadyThere && memberCount(body.project_id) >= capacityOf(body.project_id)) {
        return projectFullProblem(body.project_id)
      }
      const wasGrouped = memberships.has(repositoryId)
      memberships.set(repositoryId, { projectId: body.project_id, created_at: nowIso() })
      const membership = membershipFor(repositoryId, { embedProject: true })
      return HttpResponse.json(membership, { status: wasGrouped ? 200 : 201 })
    })
  )

export const deleteRepositoryProjectMembership = () =>
  http.delete(
    '/api/v1/repositories/:repositoryId/project-membership',
    withAuth(({ params }) => {
      if (!canManage) {
        return forbidden()
      }
      memberships.delete(String(params.repositoryId))
      return new HttpResponse(null, { status: 204 })
    })
  )

/** Every project handler, keyed the way `defaultHandlers` keys its entries. */
export const projectHandlers = () => ({
  getProjects: getProjects(),
  getProject: getProject(),
  postProject: postProject(),
  putProject: putProject(),
  deleteProject: deleteProject(),
  getProjectMemberships: getProjectMemberships(),
  postProjectMemberships: postProjectMemberships(),
  getRepositoryProject: getRepositoryProject(),
  putRepositoryProjectMembership: putRepositoryProjectMembership(),
  deleteRepositoryProjectMembership: deleteRepositoryProjectMembership(),
})
